import { PhaseAnalysis } from 'modules/Cube/domain/postActionStages/queries/getPhaseAnalysis/getPhaseAnalysis'
import type { Duration as DateFnsDuration } from '@sequencehq/utils/dates'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import { QuoteBlock } from '@sequencehq/quote-content'
import {
  CAProvinces,
  CountriesAlpha2,
  Currency,
  IntegrationServices,
  USStates
} from '@sequencehq/api/commonEnums'
import { Dashboardv20240509Api } from '@sequencehq/api/dashboard/v20240509'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import {
  Dashboardv99990101Api,
  Dashboardv99990101CommonModels
} from '@sequencehq/api/dashboard/v99990101'
import type {
  CustomerAddress,
  ServerAsset
} from '@sequencehq/api/utils/commonModels'
import type { PhaseEditingMode } from '@sequencehq/api/dashboard/v20240730'

export type SaveEntity<T> = Omit<
  T,
  'createdAt' | 'updatedAt' | 'sequenceAccountId'
>

export type DisabledField = {
  fieldName: string
  reasons: string[]
}

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object | undefined
      ? RecursivePartial<T[P]>
      : T[P]
}
export type AvailableStandardFrequency =
  | 'ONE_TIME'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'HALF_YEARLY'
  | 'YEARLY'

/**
 * Data: 'real' data - this is used to drive queries and is treated
 * as the ground truth for the editor. It is expected that this data
 * can be loaded/saved.
 *
 * Derived: this is transient state associated with the current editor.
 * For the same version of the application we expect the same data to
 * result in the same derived state.
 *
 * Configuration: all configuration data which can influence access and
 * behaviour of the application.
 */
export type CustomerAlias = {
  value: string
  id: string
  label?: string
}

export type CustomerOrganization = {
  id: string
  owner: {
    id: string
    name: string
  }
  members: {
    id: string
    name: string
  }[]
}

export type Duration = Pick<DateFnsDuration, 'years' | 'months' | 'days'>
/**
 * This data is common to all major 'root' types that cube can load,
 * which at the moment is quote and billing schedule.
 */
export enum CubeStatus {
  QuoteTemplate = 'quoteTemplate',
  QuoteDraft = 'quoteDraft',
  QuotePublished = 'quotePublished',
  QuotePreparingSignatureRequest = 'quotePreparingSignatureRequest',
  QuoteReadyToSign = 'quoteReadyToSign',
  QuoteAccepted = 'quoteAccepted',
  QuoteExecuted = 'quoteExecuted',
  ScheduleUnsaved = 'scheduleUnsaved',
  ScheduleDraft = 'scheduleDraft',
  SchedulePending = 'schedulePending',
  ScheduleActive = 'scheduleActive',
  ScheduleSuspended = 'scheduleSuspended',
  ScheduleCancelled = 'scheduleCancelled',
  ScheduleCompleted = 'scheduleCompleted'
}

export type CustomerTaxStatus = 'TAXED' | 'TAX_EXEMPT' | 'REVERSE_CHARGED'
export type Customer = {
  id: string
  status?: 'DRAFT' | 'ACTIVE'
  legalName: string
  domain?: string
  addresses: CustomerAddress[]
  contacts: Dashboardv99990101CommonModels.Customers.CustomerContact[]
  taxStatus: CustomerTaxStatus
  archivedAt?: string
  aliases: CustomerAlias[]
  integrationIds: {
    service: IntegrationServices
    id: string
    lastSynced?: string
    url?: string
  }[]
  organizations?: CustomerOrganization[]
}

export type TaxRate = {
  id: string
  name: string
  country: CountriesAlpha2
}

export type Product = {
  id: string
  name: string
  label: string
  taxCategoryId?: string
}

export type BillingFrequency =
  | 'ONE_TIME'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'YEARLY'
  | 'HALF_YEARLY'
  | 'ON_DEMAND'
  | 'PER_EVENT'
export type BillingType = 'IN_ARREARS' | 'IN_ADVANCE'

export type Price = Dashboardv20240509Api.GetPrice.Price

export type Minimum = {
  id: string
  value: string
  name: string
  scope: {
    target: 'specific' | 'allUsage'
    priceIds: Price['id'][]
  }
}

export type Discount = {
  id: string
  message: string
  discountCalculationType: 'PERCENTAGE' | 'NOMINAL'
  amount: number
  priceIds: Array<Price['id']>
  applyToAllPrices: boolean
  seatDiscountType: 'INCLUDED_SEATS_ONLY' | 'OVERAGE_SEATS_ONLY' | 'ALL_SEATS'
}

// Credit grant staged in the Cube domain, mirroring Discount/Minimum. Real id
// => relinked on save; NEW_CREDIT_GRANT id => created. `priceIds` empty =>
// every price in the phase.
export type CreditGrant = {
  id: string
  name: string
  creditUnitType: Dashboardv99990101Api.CreditGrants.CreditUnitType
  currency?: Currency
  metricId?: string
  amount: number
  costOfCredit?: number
  taxRateId?: string
  grantRefreshFrequency: Dashboardv99990101Api.CreditGrants.GrantRefreshFrequency
  // Absent means the platform charges on the refresh cadence.
  billingFrequency?: Dashboardv99990101Api.CreditGrants.GrantBillingFrequency
  rolloverMode: Dashboardv99990101Api.CreditGrants.RolloverMode
  priceIds: Array<Price['id']>
  // Absent means the grant posts to the account's default NetSuite item.
  netsuiteItemId?: string
}

export type PhaseRecurrencePreference = 'CONTINUE_FROM_PREVIOUS_PHASE' | 'RESET'
export type PhaseDuration = Duration | 'OPEN_ENDED' | 'MILESTONE'
export type PhasePriceMetadata = {
  priceId: Price['id']
  arrCalculation: 'INCLUDE' | 'EXCLUDE'
}
export type ContractReferenceType =
  DashboardApi20240730.BillingSchedules.ContractReferenceType

/**
 * A quote, opportunity, NetSuite contract tag or other reference on a phase.
 * `label` is what we show. It comes from the API, or from the user's own input
 * for a reference added in this session. `url` only ever comes from the API, so
 * a newly picked quote has no link until the schedule is saved.
 */
export type ContractReference = {
  type: ContractReferenceType
  value: string
  label: string
  url: string | null
}

export type Phase = {
  id: string
  priceIds: Array<Price['id']>
  discountIds: Array<Discount['id']>
  name: string
  externalContractRef?: string
  /** Only loaded while the `contractReferenceEditing` feature is on. */
  contractReferences?: Array<ContractReference>
  minimumIds: Array<Minimum['id']>
  creditGrantIds: Array<CreditGrant['id']>
  duration: PhaseDuration
  recurrencePreference: PhaseRecurrencePreference
  phasePriceMetadata: Array<PhasePriceMetadata>
}

export type Common = {
  /** @remarks The entity ID, such as the quote or schedule `id` property */
  id: string
  title: string
  /** @remarks The human readable identifier for the root entity, such as the quote number. */
  alias: string
  customerId: Customer['id']
  /**
   * @remarks Cube always models currency globally - so any indirect currency select (such as
   * via adding a price) should update this value too
   */
  currency?: Currency
  /**
   * @remarks Cube's status is a combination of the statuses across the (currently) two root
   * entities, and is the only point in the data model where we 'fork' between the two in the
   * data model.
   */
  status: CubeStatus
  /**
   * @remarks The start date, if present 'anchors' the calculation of relative dates which allows
   * us to produce absolute values from their relative definitions, including the end date (which
   * is why we don't store the end date explicitly, as that is driven by the final date of the last
   * phase).
   */
  startDate: Date | undefined
  isArchived: boolean
  /**
   * @remarks We only reference the ids of entities, with the actual entity itself being contained within
   * the appropriate data object. This makes lookup and modification of these entities more simple, and
   * we can produce pre-resolved views in the query layer.
   */
  phaseIds: Array<Phase['id']>
  createdAt: Date | undefined
  /**
   * Set on a schedule that an amendment quote is holding. The API rejects every
   * edit while it is set, so the editor goes read only and says which quote to
   * go and look at. Quotes and templates never carry it.
   */
  lockedByQuoteId?: string
}

export type ScheduleCustomField = {
  /**
   * Stable identifier used to track the field across renders. For new fields
   * this is a client-generated id; for persisted fields this is the server id.
   */
  id: string
  key: string
  value: string
}

export type Schedule = {
  /**
   * @remarks AKA. the billing date. When phases contain multiple billing frequencies, we will set this to
   * match the start date and not allow user modification
   */
  recurrenceDayOfMonth: number
  purchaseOrderNumber: string
  reference: string
  label: string
  rollUpBilling: boolean
  stripePayment: boolean
  stripeAutoCharge: boolean
  goCardlessPayment: boolean
  taxRateId: TaxRate['id']
  autoIssueInvoices: boolean
  attachmentAssets: ServerAsset[]
  defaultDueDateDays: number | null
  memo: string
  customFields: ScheduleCustomField[]
  /**
   * Set once the user edits any phase's contract references. From then on every
   * save sends the full list on every phase, because a list on one phase
   * replaces the references of the whole schedule. Until then saves leave them
   * alone.
   */
  replaceContractReferences: boolean
}

export type NotificationHistoryEntry =
  DashboardApi20240730.Quotes.NotificationHistoryEntry

export type QuoteContact = {
  contactId: string
  name: string | undefined
  email: string
  /** Optional to match the API type, where a cc contact never carries one. */
  signedAt?: string
  notificationHistory?: NotificationHistoryEntry[]
}

export type CounterSigner = {
  id: string
  name: string
  email: string
  signedAt: string | undefined
  notificationHistory?: NotificationHistoryEntry[]
}

export type QuotePdfStatus =
  | 'NOT_YET_REQUESTED'
  | 'REQUESTED'
  | 'IN_PROGRESS'
  | 'FAILED'
  | 'GENERATED'

export type QuoteDealType = DashboardApi20240730.Quotes.QuoteDealType
export type QuoteExecuteScheduleAction =
  DashboardApi20240730.PostQuoteExecute.QuoteExecuteScheduleAction
export type Quote = {
  contractLength?: Duration
  dealType: QuoteDealType
  /** @remarks how long after publishing the quote will expire */
  expiresIn: Duration | undefined
  acceptedAt: Date | undefined
  publishedAt: Date | undefined
  readyToSignAt: Date | undefined
  executedAt: Date | undefined
  billingScheduleId: string | undefined
  amendment:
    | {
        billingScheduleId: string
        effectiveFrom: Date
        /**
         * The quote that was accepted for this schedule amendment instead of this one. Once it is
         * set this amendment can never be executed, so the editor goes read only and offers only
         * archiving.
         */
        supersededByQuoteId?: string
        /**
         * Another amendment quote of this schedule that is out for signature or signed. Only one
         * can be at a time, so this quote can't be sent for signature or marked as signed yet.
         */
        outForSignatureQuoteId?: string
        original: {
          phases: Array<
            Pick<
              DashboardApi20240730.Quotes.QuoteAmendmentSnapshotPhase,
              'startDate' | 'endDate' | 'prices' | 'discounts' | 'minimums'
            >
          >
        }
      }
    | undefined
  expiresAt: Date | undefined
  quoteNumber: string
  createdByEmail: string | undefined
  contacts: QuoteContact[]
  ccContacts: QuoteContact[]
  isSignaturesEnabled: boolean
  counterSigners: CounterSigner[]
  isInternalSignerEnabled?: boolean
  customerLegalName: string | undefined
  customerAddress: Address | undefined
  salesforceOpportunityId?: string
  integrationIds: {
    service: IntegrationServices
    id: string
    url?: string
  }[]
  pdfStatus: QuotePdfStatus
  attachmentAssets: ServerAsset[]
  isExpired: boolean
  defaultDueDateDays: number | undefined
  isAutoExecuteQuotesEnabled: boolean
  /** Account-wide: quote recipients must sign in before they can open a quote. */
  isCustomerPortalAuthRequired: boolean
  signatureMode: 'EMBEDDED' | 'NON_EMBEDDED' | undefined
  activeQuoteApprovalAutomationVersionId: string | undefined
  runningQuoteApprovalAutomationExecutionId: string | undefined
  runningQuoteApprovalAutomationExecutionCreateTime: string | undefined
}

export type PresentationV3 = {
  version: 3
  blocks: Array<QuoteBlock>
  settings: PresentationV3Settings
  metadata?: {
    totalContractValue: number
    totalDiscountValue: number
  }
}

type PresentationV3Settings = {
  showTotals: boolean
  groupPricesByPhase: boolean
  showPdfCoverPage: boolean
  showProductDescriptions: boolean
  /**
   * This may be undefined in some scenarios, such as a result
   * of skipping the presentation upgrade process (which is a workaround
   * to prevent issues with 'mostly read only' state quotes - see
   * quote.adapters.in.ts for more details). So, capture that possibility
   * here so we can work around it where needed.
   */
  priceEditorProductOrder?: Array<Product['id']>
  showMonthlyRateForAnnualPrices?: boolean
  columns?: {
    quantity?: boolean
    lineTotal?: boolean
    billingPeriods?: boolean
    billingType?: boolean
  }
  showImagesInPdfBody?: boolean
  hideOpenEndedPhases?: boolean
  seatBasedMinimumText?: string
}

export type Address =
  | {
      line1: string
      line2?: string
      town: string
      postcode: string
      country: CountriesAlpha2
    }
  | {
      line1: string
      line2?: string
      town: string
      state: USStates
      postcode: string
      country: 'US'
    }
  | {
      line1: string
      line2?: string
      town: string
      state: CAProvinces
      postcode: string
      country: 'CA'
    }

export type MerchantBranding = {
  legalCompanyName: string
  address: Address
  email: string
  primaryColour?: string
  logoUrl?: string
  url?: string
}

export type ListPrice = {
  id: string
  name: string
  productId: string
  structure: Price['structure']
  currency: Currency
  integrationIds: Price['integrationIds']
  billingFrequency: BillingFrequency
  usageCalculationPeriod?: Price['usageCalculationPeriod']
  billingType: BillingType
  customMetricParameters: Price['customMetricParameters']
  createdAt: string
}

export type Contact = {
  id: string
  name?: string
  email: string
  customerId: string
}

export type ContactPostBody = {
  name: string
  email: string
  purposes: Array<'BILLING' | 'SHIPPING'>
  notificationSubscriptions: Array<'BILLING'>
}

export type CubeEditorData = {
  common: Common
  schedule: Schedule
  quote: Quote
  presentation: PresentationV3
  phases: Record<Phase['id'], Phase>
  customers: Record<Customer['id'], Customer | undefined>
  taxRates: Record<TaxRate['id'], TaxRate>
  products: Record<Product['id'], Product>
  discounts: Record<Discount['id'], Discount>
  prices: Record<Price['id'], Price>
  minimums: Record<Minimum['id'], Minimum>
  creditGrants: Record<CreditGrant['id'], CreditGrant>
  merchantBranding: MerchantBranding
}

export enum DisabledReasonType {
  CompletedSchedule = 'completedSchedule',
  PhaseNotStarted = 'phaseNotStarted',
  CompletedPhase = 'completedPhase',
  StartedPhase = 'startedPhase',
  TieredPriceTransition = 'tieredPriceTransition',
  PhaseStartTransition = 'phaseStartTransition',
  PhaseEndTransition = 'phaseEndTransition',
  InvalidStatus = 'invalidStatus',
  PhaseHasNoPrices = 'phaseHasNoPrices',
  MaxMinimums = 'maxMinimums',
  NoValidPrices = 'noValidPrices',
  NoValidDiscounts = 'noValidDiscounts',
  GlobalDiscountExists = 'globalDiscountExists',
  FeatureDisabled = 'featureDisabled',
  NotFirstPhase = 'notFirstPhase',
  TooManyPhases = 'tooManyPhases',
  SavingInProgress = 'savingInProgress',
  NonMonthlyPricesPresent = 'nonMonthlyPricesPresent',
  InvalidDuration = 'noDuration',
  InvalidBillingType = 'invalidBillingType',
  NoProration = 'noProration',
  CantChangeAfterPublish = 'cantChangeAfterPublish',
  CantChangeWhenPreparingSignatureRequest = 'cantChangeWhenPreparingSignatureRequest',
  CantChangeWhenArchived = 'cantChangeWhenArchived',
  CantChangeWhenAccepted = 'cantChangeWhenAccepted',
  CantChangeWhenExecuted = 'cantChangeWhenExecuted',
  CantChangeWhenExpired = 'cantChangeWhenExpired',
  CantEditPricingModelOnQuotePrice = 'cantEditPricingModelOnQuotePrice',
  NoChangesMade = 'noChangesMade',
  NoCustomerSelected = 'noCustomerSelected',
  NoCustomerSalesforceLink = 'noCustomerSalesforceLink',
  SalesforceOpportunityLinked = 'salesforceOpportunityLinked',
  NotMilestonePhase = 'notMilestonePhase',
  ScheduleNotActive = 'scheduleNotActive',
  IsFirstPhase = 'isFirstPhase',
  CannotEditBillingSchedule = 'cannotEditBillingSchedule',
  IsOnlyPhase = 'isOnlyPhase',
  PendingApproval = 'pendingApproval',
  ConflictingPaymentProvider = 'conflictingPaymentProvider',
  GoCardlessRequiresAtLeastOneProduct = 'goCardlessRequiresAtLeastOneProduct',
  CantChangeOnAmendmentDealType = 'cantChangeOnAmendmentDealType',
  BeforeAmendmentEffectiveFrom = 'beforeAmendmentEffectiveFrom',
  AmendedScheduleCovered = 'amendedScheduleCovered',
  InvalidDealType = 'invalidDealType',
  ScheduleLockedByAmendment = 'scheduleLockedByAmendment',
  MaxCreditGrants = 'maxCreditGrants',
  QuoteSuperseded = 'quoteSuperseded',
  AmendmentOutForSignature = 'amendmentOutForSignature'
}

export type DisabledReason = {
  reasonType: DisabledReasonType
  /**
   * The metadata is designed more for debugging than for programmatic access
   * in this case.
   */
  metadata: Record<string, unknown>
}

export type VisibleEnabledFeatureAvailable = {
  available: {
    visible: boolean
    enabled: boolean
  }
  reasons: Array<DisabledReason>
}

export type AmendmentContractValues = {
  original: number
  amended: number
  adjustment: number
}

export type ResolvedPhase = {
  id: Phase['id']
  name: string
  externalContractRef?: string
  contractReferences: Array<ContractReference>
  minimums: Minimum[]
  discounts: Discount[]
  creditGrants: CreditGrant[]
  prices: Price[]
  products: Product[]
  dates: {
    absolute: {
      start?: Date
      end?: Date
    }
    duration: PhaseDuration
  }
  globalDiscount: Discount | undefined
  analysis: PhaseAnalysis
  phaseHasStarted: boolean
  isBeforeAmendmentEffectiveFrom: boolean
  recurrencePreference: PhaseRecurrencePreference
  recurrenceDayOfMonth: number
  phasePriceMetadata: Phase['phasePriceMetadata']
}

export type AvailableProductFeatures = {
  edit: VisibleEnabledFeatureAvailable
  delete: VisibleEnabledFeatureAvailable
  editDiscount: VisibleEnabledFeatureAvailable
  deleteDiscount: VisibleEnabledFeatureAvailable
  canEditPricingModel: VisibleEnabledFeatureAvailable
  editPhasePriceMetadata: VisibleEnabledFeatureAvailable
}
export type AvailableFeaturesForPhase = {
  products: Record<Product['id'], AvailableProductFeatures>
  phase: {
    delete: VisibleEnabledFeatureAvailable
    editName: VisibleEnabledFeatureAvailable
    contractTag: VisibleEnabledFeatureAvailable
    contractReferences: VisibleEnabledFeatureAvailable
    editPhaseRecurrence: VisibleEnabledFeatureAvailable
    completeMilestone: VisibleEnabledFeatureAvailable
    discount: {
      add: VisibleEnabledFeatureAvailable
      edit: VisibleEnabledFeatureAvailable
      delete: VisibleEnabledFeatureAvailable
    }
    creditGrant: {
      add: VisibleEnabledFeatureAvailable
    }
    product: {
      add: VisibleEnabledFeatureAvailable
      reorder: VisibleEnabledFeatureAvailable
    }
    insert: {
      before: VisibleEnabledFeatureAvailable
      after: VisibleEnabledFeatureAvailable
    }
    minimum: {
      add: VisibleEnabledFeatureAvailable
      edit: VisibleEnabledFeatureAvailable
      delete: VisibleEnabledFeatureAvailable
    }
    dates: {
      editStart: VisibleEnabledFeatureAvailable
      editDuration: VisibleEnabledFeatureAvailable
      alignDates: VisibleEnabledFeatureAvailable
    }
  }
}

export type ValidationResult = {
  message: string
  metadata?: Record<string, unknown>
}

export type ValidationResults = {
  common: {
    currency: ValidationResult[] | null
    customerId: ValidationResult[] | null
    phaseIds: ValidationResult[] | null
    phases: Record<
      Phase['id'],
      {
        prices: ValidationResult[] | null
        duration: ValidationResult[] | null
      }
    > | null
    startDate: ValidationResult[] | null
    title: ValidationResult[] | null
  }
  schedule: {
    recurrenceDayOfMonth: ValidationResult[] | null
    taxRate: ValidationResult[] | null
    defaultDueDateDays: ValidationResult[] | null
    memo: ValidationResult[] | null
  }
  quote: {
    dealType: ValidationResult[] | null
    expiry: ValidationResult[] | null
    presentation: ValidationResult[] | null
    quoteNumber: ValidationResult[] | null
    contactIds: ValidationResult[] | null
    counterSigners: ValidationResult[] | null
    contractDuration: ValidationResult[] | null
    amendmentDate: ValidationResult[] | null
  }
}

/**
 * The subset of cube state that schedule validation reads. Extracted so the
 * same validation logic can run against a synthetic schedule (e.g. an existing
 * schedule with a renewal quote's phases appended) and not only the reducer's
 * live `prevState`.
 */
export type ScheduleValidationData = Pick<
  CubeReducerState,
  'data' | 'queries' | 'configuration'
>

export type CubeReducerState = {
  data: CubeEditorData
  queries: {
    resolvedPhases: Record<Phase['id'], ResolvedPhase>
    orderedPhases: ResolvedPhase[]
    selectedCurrency?: Currency
    pricingDates: {
      start: Date | undefined
      end: Date | undefined
    }
    totalPricingDuration: Duration | undefined
    amendment: {
      phaseId: Phase['id'] | undefined
      remainingDuration: PhaseDuration | undefined
      isScheduleCovered: boolean
      contractValues: AmendmentContractValues | undefined
    }
    cubeDataUpdated: boolean
    availableFrequencies: AvailableStandardFrequency[]
    availableTaxRates: Record<TaxRate['id'], TaxRate>
    validation: {
      allValidationResults: {
        save: ValidationResults
        publish: ValidationResults
        accept: ValidationResults
        execute: ValidationResults
      }
      validationErrorsPresent: {
        save: boolean
        publish: boolean
        accept: boolean
        execute: boolean
        deleteLatestDraft: boolean
      }
      activeValidationResults: ValidationResults | null
    }
    availableFeatures: {
      phases: Record<Phase['id'], AvailableFeaturesForPhase>
      schedule: {
        publish: VisibleEnabledFeatureAvailable
        save: VisibleEnabledFeatureAvailable
        stripePayment: VisibleEnabledFeatureAvailable
        stripeAutoCharge: VisibleEnabledFeatureAvailable
        goCardlessPayment: VisibleEnabledFeatureAvailable
        rollUpBilling: VisibleEnabledFeatureAvailable
        recurrenceDayOfMonth: VisibleEnabledFeatureAvailable
        taxRate: VisibleEnabledFeatureAvailable
        label: VisibleEnabledFeatureAvailable
        defaultDueDateDays: VisibleEnabledFeatureAvailable
        memo: VisibleEnabledFeatureAvailable
        reference: VisibleEnabledFeatureAvailable
        purchaseOrderNumber: VisibleEnabledFeatureAvailable
        autoIssueInvoices: VisibleEnabledFeatureAvailable
      }
      quote: {
        contractLengthEditing: VisibleEnabledFeatureAvailable
        publish: VisibleEnabledFeatureAvailable
        accept: VisibleEnabledFeatureAvailable
        /** Sending signature requests for a published quote, which makes it ready to sign. */
        readyToSign: VisibleEnabledFeatureAvailable
        execute: VisibleEnabledFeatureAvailable
        preview: VisibleEnabledFeatureAvailable
        archive: VisibleEnabledFeatureAvailable
        deleteLatestDraft: VisibleEnabledFeatureAvailable
        edit: VisibleEnabledFeatureAvailable
        setRecipients: VisibleEnabledFeatureAvailable
        duplicate: VisibleEnabledFeatureAvailable
        revoke: VisibleEnabledFeatureAvailable
        integrations: VisibleEnabledFeatureAvailable
        saveTemplate: VisibleEnabledFeatureAvailable
        createQuoteFromTemplate: VisibleEnabledFeatureAvailable
        editPaymentTerms: VisibleEnabledFeatureAvailable
        editCurrency: VisibleEnabledFeatureAvailable
        editCountersigners: VisibleEnabledFeatureAvailable
        editAmendmentEffectiveFrom: VisibleEnabledFeatureAvailable
      }
      common: {
        editCustomer: VisibleEnabledFeatureAvailable
        editStartDate: VisibleEnabledFeatureAvailable
        milestoneBilling: VisibleEnabledFeatureAvailable
        addPhase: VisibleEnabledFeatureAvailable
        inspector: {
          unlinkSalesforceCustomer: VisibleEnabledFeatureAvailable
          linkSalesforceCustomer: VisibleEnabledFeatureAvailable
        }
      }
    }
    quote: {
      isATemplate: boolean
    }
  }
  configuration: {
    currency: {
      default: Currency
      enabled: Currency[]
    }
    permissions: Record<string, boolean>
    availableFrequencies: AvailableStandardFrequency[]
    features: {
      showNewTaxManagement: boolean
      stripeIntegrationAvailable: boolean
      goCardlessIntegrationAvailable: boolean
      showReferenceField: boolean
      showLabelField: boolean
      useMilestoneBilling: boolean
      phaseRecurrenceEditing: boolean
      phasePriceMetadataEditing: boolean
      creditGrantsEditing: boolean
      /**
       * The legacy NetSuite contract tag. While `contractReferenceEditing` is on
       * this only says whether NetSuite is connected, which decides whether the
       * NetSuite reference type is shown.
       */
      contractTagEditing: boolean
      contractReferenceEditing: boolean
      allowSingleDayDuration: boolean
      stackedDiscounts: boolean
    }
    /**
     * Account-level choices the user made in settings, which the editor starts
     * from. These are deliberately not in `features` - a feature says what the
     * editor is allowed to do, whereas these say what it does by default.
     */
    editorDefaults: {
      phaseEditingMode: PhaseEditingMode
      resetBillingPeriod: PhaseRecurrencePreference
    }
  }
  editor: {
    loaded: boolean
    lastLoadedAt?: Date
    activeValidationSet:
      | 'save'
      | 'publish'
      | 'accept'
      | 'execute'
      | 'deleteLatestDraft'
      | undefined
    savingDraft: boolean
    savingSchedule: boolean
    highlightedEntityIds: Array<string>
    quoteEditorNeedsReinitialization: boolean
  }
  /**
   * This is used for some niche queries related to the initial state of
   * the editor, such as whether or not the version initial had a price on
   * it. It is only to be accessed, exclusively, by queries, and set by the
   * load action.
   */
  initialData: CubeEditorData
  initialQueries: {
    resolvedPhases: Record<Phase['id'], ResolvedPhase>
  }
}

export type Action<T extends string, P> = {
  type: T
  payload: P
}

export enum CorePortErrors {
  NotFound = 'notFound',
  Other = 'other'
}

export type CubeDomainInput = Pick<CubeReducerState, 'data' | 'configuration'>

export type LoadAction = Action<'load', CubeDomainInput>

export type ResetAction = Action<'reset', undefined>

export type AlignPhaseDurationAction = Action<
  'alignPhaseDuration',
  {
    phaseId: Phase['id']
  }
>

export type UpdateAction = Action<
  'updateData',
  RecursivePartial<CubeReducerState['data']>
>

export type DeleteAction = Action<
  'deleteData',
  {
    type: keyof CubeEditorData
    ids: string[]
  }
>

export type UpdateConfigurationAction = Action<
  'updateConfiguration',
  RecursivePartial<CubeReducerState['configuration']>
>

export type UpdateEditorAction = Action<
  'updateEditor',
  Partial<CubeReducerState['editor']>
>

export type CubeActions =
  | UpdateConfigurationAction
  | UpdateAction
  | UpdateEditorAction
  | AlignPhaseDurationAction
  | LoadAction
  | DeleteAction
  | ResetAction

/**
 * =============================
 **/

export type ActionHandler<A extends CubeActions> = (
  prevState: CubeReducerState
) => (action: A) => CubeReducerState

export type PostActionContext = {
  preActionState: CubeReducerState
  action: CubeActions
}
export type PostActionStage = (
  ctx: PostActionContext
) => (prevState: CubeReducerState) => CubeReducerState

export type SchedulePreviewArguments = {
  startDate: string
  endDate: string
  recurrenceDayOfMonth: number
  prices: Array<{
    billingFrequency: BillingFrequency
    billingType: BillingType
  }>
}

export type SchedulePreviewResponse = Array<{
  start: string
  end: string
}>

/**
 * Port implementation type
 */
export type ScheduleCubePorts = {
  in: {
    core: () => Promise<{
      data: CubeDomainInput | null
      error: CorePortErrors | null
    }>
    customer: (args: { customerId: Customer['id'] }) => Promise<Customer | null>
    schedule: {
      preview: (
        args: SchedulePreviewArguments
      ) => Promise<SchedulePreviewResponse>
    }
  }
  out: {
    save: (data: CubeDomainInterface['queries']) => Promise<{
      success: boolean
      id?: string
    }>
    archive: () => Promise<boolean>
    schedule: {
      activate: (data: CubeDomainInterface['queries']) => Promise<{
        success: boolean
        id?: string
      }>
    }
  }
}

export type QuoteCubePorts = {
  in: {
    core: () => Promise<{
      data: CubeDomainInput | null
      error: CorePortErrors | null
    }>
    customer: (args: { customerId: Customer['id'] }) => Promise<Customer | null>
  }
  out: {
    save: (data: CubeDomainInterface['queries']) => Promise<{
      success: boolean
      id?: string
    }>
    archive: () => Promise<boolean>
    quote: {
      publish: (data: {
        /** The emails to send the published quote to. */
        recipients: string[] | undefined
        sendRecipientsEmail: boolean
        emailMessage: string | undefined
        makeReadyToSign: boolean
        messageForApprover: string | undefined
        /**
         * Wait for the approval workflow to settle before reporting back,
         * because the modal said "Publish" rather than "Send for review".
         */
        awaitApprovalOutcome: boolean
      }) => Promise<{ outcome: 'PUBLISHED' | 'APPROVAL_REQUESTED' } | false>
      accept: () => Promise<boolean>
      execute: (scheduleAction: QuoteExecuteScheduleAction) => Promise<boolean>
      deleteLatestDraft: () => Promise<boolean>
      duplicate: () => Promise<{ newQuoteId: string } | null>
    }
  }
}

export type QuoteTemplateCubePorts = {
  in: {
    core: () => Promise<{
      data: CubeDomainInput | null
      error: CorePortErrors | null
    }>
    customer: (args: { customerId: Customer['id'] }) => Promise<Customer | null>
  }
  out: {
    save: (data: CubeDomainInterface['queries']) => Promise<{
      success: boolean
      id?: string
    }>
    archive: () => Promise<boolean>
  }
}

export type CubePorts =
  | ScheduleCubePorts
  | QuoteCubePorts
  | QuoteTemplateCubePorts

export type CubePortImplementationProp = {
  configuration: CubeReducerState['configuration']
  defaultValues?: {
    customerId?: string
    salesforceOpportunityId?: string
    quoteTemplateId?: string
  }
}

export type CubePortImplementation = (
  ctx: CubePortImplementationProp
) => CubePorts
